import api, { post, get, patch, put, del, parseApiError, type ApiError } from './api'
import type {
  User,
  ApiCurrentUser,
  AuthTokens,
  AccessTokenResponse,
  LoginCredentials,
  RegisterData,
  RegisterResponse,
  UserProfile,
  OnboardingData,
  RoutePreferences,
} from '@/types/auth'

export const authService = {
  /**
   * Register a new user account
   */
  async register(data: RegisterData): Promise<RegisterResponse> {
    try {
      return await post<RegisterResponse>('/auth/register', data)
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    try {
      return await post<AuthTokens>('/auth/login', credentials)
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Logout and invalidate tokens
   */
  async logout(): Promise<void> {
    try {
      await post('/auth/logout')
    } catch {
      // Ignore logout errors - we'll clear local state anyway
    }
  },

  /**
   * Refresh access token using refresh token cookie.
   *
   * Type-drift guard correction (2026-08-24): the refresh wire shape is
   * AccessTokenResponse (token only), not the login-shaped AuthTokens.
   */
  async refreshToken(): Promise<AccessTokenResponse> {
    try {
      return await post<AccessTokenResponse>('/auth/refresh')
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Resend email verification link
   */
  async resendVerification(email: string): Promise<void> {
    try {
      await post('/auth/resend-verification', { email })
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Verify email with token from email link
   */
  async verifyEmail(token: string): Promise<AuthTokens> {
    try {
      return await post<AuthTokens>('/auth/verify-email', { token })
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<void> {
    try {
      await post('/auth/forgot-password', { email })
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Reset password with token from email — returns new access token on success
   */
  async resetPassword(token: string, newPassword: string): Promise<AuthTokens> {
    try {
      return await post<AuthTokens>('/auth/reset-password', { token, new_password: newPassword })
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Get current authenticated user
   *
   * SR-34: `GET /users/me` does NOT return the shape `User` declares. It returns
   * `tier` (not `subscription_tier`) and nests onboarding under `profile`, so
   * `get<User>('/users/me')` was a cast, not a conversion — every field the type
   * promised but the response lacked silently read `undefined`.
   *
   * The consequence was not cosmetic: `hasCompletedOnboarding` is
   * `user.onboarding_completed`, so it was permanently false and the router's
   * onboarding guard bounced every authenticated user away from every
   * `requiresAuth` page — My Routes included, which is why SR-33's fix could not
   * be seen in a browser. Normalising here rather than at the three call sites
   * because this is the boundary that owns the wire format.
   */
  async getCurrentUser(): Promise<User> {
    try {
      const raw = await get<ApiCurrentUser>('/users/me')
      return {
        id: raw.id,
        email: raw.email,
        email_verified: raw.email_verified,
        subscription_tier: raw.tier,
        onboarding_completed: raw.profile?.onboarding_completed ?? false,
        created_at: raw.created_at ?? '',
        run_history_consent_at: raw.run_history_consent_at ?? null,
      }
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Get user profile
   */
  async getProfile(): Promise<UserProfile> {
    try {
      return await get<UserProfile>('/users/me/profile')
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    try {
      return await patch<UserProfile>('/users/me/profile', data)
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Get route preferences
   */
  async getPreferences(): Promise<RoutePreferences> {
    try {
      return await get<RoutePreferences>('/users/me/preferences')
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Update route preferences (full PUT)
   */
  async updatePreferences(data: Partial<RoutePreferences>): Promise<RoutePreferences> {
    try {
      return await put<RoutePreferences>('/users/me/preferences', data)
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Complete onboarding questionnaire
   */
  async completeOnboarding(data: OnboardingData): Promise<UserProfile> {
    try {
      return await post<UserProfile>('/users/me/onboarding', data)
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Delete user account (requires password confirmation)
   */
  async deleteAccount(password: string): Promise<void> {
    try {
      await del('/users/me', { password })
    } catch (error: any) {
      throw parseApiError(error)
    }
  },

  /**
   * Art. 20 data export (spec 047, closes SR-44).
   *
   * The endpoint returns the full export document synchronously with a
   * Content-Disposition attachment header — not the async "we'll email you"
   * shape this function's old signature imagined. Returns the document as a
   * Blob plus the server-suggested filename so the page can trigger a save.
   *
   * On 429 (per-user daily cap) the thrown error carries `retryAfterSeconds`
   * from the Retry-After header.
   */
  async requestDataExport(): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await api.post('/users/me/data-export', undefined, {
        responseType: 'blob',
      })
      const disposition: string = response.headers['content-disposition'] ?? ''
      const match = /filename="?([^";]+)"?/.exec(disposition)
      const filename =
        match?.[1] ?? `move78-data-export-${new Date().toISOString().slice(0, 10)}.json`
      return { blob: response.data as Blob, filename }
    } catch (error: any) {
      const retryAfter = Number(error?.response?.headers?.['retry-after'])
      const parsed = parseApiError(error) as ApiError & { retryAfterSeconds?: number }
      if (error?.response?.status === 429 && Number.isFinite(retryAfter)) {
        parsed.retryAfterSeconds = retryAfter
      }
      throw parsed
    }
  },
}

export default authService
